import setPurchaseHistoryConfig from "@NA/Handlers/PurchaseHistory/SetPurchaseHistoryConfig";
import { useOutsideAlerter } from "@NA/Hooks/useOutsideAlerter";
import DropDown from "@NA/Icons/DropDown";
import CustomApplicationState from "@NA/Store/Data/CustomApplicationState/CustomApplicationState";
import { defaultPaginationValues } from "@NA/Store/Reducer/ADIMyPurchaseHistoryConfigReducer";
import ADIColors from "@NA/Utils/ADIColors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button from "@insite/mobius/Button";
import DatePicker, { DatePickerPresentationProps, DatePickerState } from "@insite/mobius/DatePicker";
import { FormFieldStyle, FormInputWrapper } from "@insite/mobius/FormField";
import Select from "@insite/mobius/Select";
import TextField from "@insite/mobius/TextField";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { getProp } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import React, { useState } from "react";
import { FC } from "react";
import { ResolveThunks, connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomApplicationState) => ({
    myPurchaseHistory: state.ADIGlobalData.myPurchaseHistoryStore.myPurchaseHistory,
    myPurchaseHistoryConfig: state.ADIGlobalData.myPurchaseHistoryConfig,
});

const mapDispatchToProps = {
    setPurchaseHistoryConfig,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface PurchaseHistoryProductSearchStyles {
    wrapper?: InjectableCss;
    searchFieldWrapper?: InjectableCss;
    dateRangeWrapper?: InjectableCss;
    dropdownContainer?: InjectableCss;
    dropdownValue?: InjectableCss;
    customDateWrapper?: InjectableCss;
    fromDateWrapper?: InjectableCss;
    toDateWrapper?: InjectableCss;
    fromDateStyles?: DatePickerPresentationProps;
    toDateStyles?: DatePickerPresentationProps;
    btnWrapper?: InjectableCss;
    clearBtnWrapper?: InjectableCss;
    searchBtnWrapper?: InjectableCss;
    selectOptions?: InjectableCss;
    icon?: TypographyProps;
    dropdownLabel?: InjectableCss;
    dropdownList?: InjectableCss;
    dropdownValues?: InjectableCss;
}

export const purchaseHistoryProductSearchStyles: PurchaseHistoryProductSearchStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: row;
            margin-top: 20px;
            margin-bottom: 20px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-wrap: wrap;
                        margin-top: 10px;
                    `,
                    css`
                        flex-wrap: wrap;
                        margin-top: 10px;
                    `,
                    css`
                        flex-wrap: wrap;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    searchFieldWrapper: {
        css: css`
            padding-right: 20px;
            width: 30%;
            max-width: 400px;
            > ${FormFieldStyle} {
                > ${FormInputWrapper} {
                    input {
                        border: 1px solid ${ADIColors.gray3};
                        &:focus {
                            box-shadow: none;
                            outline: 1px solid ${ADIColors.gray3};
                            outline-offset: -1px;
                            border-radius: 6px;
                            padding: 9px 15px;
                            border: 1px solid transparent;
                        }
                    }
                }
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: 100%;
                        max-width: 100%;
                        padding-right: 0px;
                    `,
                    css`
                        width: 100%;
                        max-width: 100%;
                        padding-right: 0px;
                    `,
                    css`
                        width: calc(100% - 200px);
                        max-width: calc(100% - 200px);
                    `,
                    null,
                    null,
                ])}
        `,
    },
    dateRangeWrapper: {
        css: css`
            width: 25%;
            padding-right: 20px;
            margin-right: 0;
            margin-top: 7px;
            line-height: normal !important;
            > ${FormFieldStyle} {
                > ${FormInputWrapper} {
                    select {
                        border: 1px solid ${ADIColors.gray3};
                        &:focus {
                            box-shadow: none;
                            outline: 1px solid ${ADIColors.gray3};
                            outline-offset: -1px;
                            border-radius: 6px;
                            padding: 9px 15px;
                            padding-right: 32px;
                            border: 1px solid transparent;
                        }
                    }
                }
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: 100%;
                        max-width: 100%;
                        padding-right: 0px;
                        margin-top: 10px;
                    `,
                    css`
                        width: 100%;
                        max-width: 100%;
                        padding-right: 0px;
                        margin-top: 10px;
                    `,
                    css`
                        width: 200px;
                        max-width: 200px;
                        padding-right: 0px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    dropdownContainer: {
        css: css`
           position: relative;
           width: 100%;
           `,
    },
    dropdownValue: {
        css: css`
            width: 100%;
            border: 1px solid;
            border-radius: 6px;
            padding: 9px 9px 9px 15px;
            height: 40px;
            background: ${ADIColors.white};
            border: 1px solid ${ADIColors.gray3};
            font-size: ${getProp("theme.typography.headerTertiary.size")};
            cursor: pointer;
            position: relative;
            display: flex;
            align-items: normal;
            justify-content: space-between;
        `,
    },
    icon: {
        css: css`
            transform: translateY(-1px) scale(0.9);
            svg {
                stroke: ${ADIColors.darkBlue1};
            }
        `,
    },
    dropdownLabel: {
        css: css`
            font-weight: 700;
            text-transform: capitalize;
            font-size: ${getProp("theme.typography.headerTertiary.size")};
            margin-bottom: 6px;
        `,
    },
    dropdownList: {
        css: css`
            border-radius: 0 0 7px 7px;
            box-shadow: 1px 2px 14px 4px rgba(57, 57, 58, 0.25);
            background: ${ADIColors.white};
            position: absolute;
            width: 100%;
            top: 90%;
            z-index: 8;
        `,
    },
    dropdownValues: {
        css: css`
            min-height: 40px;
            border-bottom: 1px solid ${ADIColors.gray3};
            font-weight: 600;
            padding: 13px 12px;
            font-size: ${getProp("theme.typography.headerTertiary.size")};
            cursor: pointer;
    
            &:last-child {
                border-bottom: 0;
                border-radius: 0 0 7px 7px;
            }
    
            &.active,
            &:hover {
                background-color: ${ADIColors.blue1};
                color: ${ADIColors.white};
                div img {
                    filter: brightness(0) invert(1);
                }
            }
        `,
    },
    customDateWrapper: {
        css: css`
            width: 45%;
            max-width: 500px;
            display: flex;
            .react-datetime-picker {
                padding: 0 15px !important;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: 100%;
                        max-width: 100%;
                        margin-top: 10px;
                    `,
                    css`
                        width: 100%;
                        max-width: 100%;
                        margin-top: 10px;
                    `,
                    css`
                        width: 50%;
                        max-width: 50%;
                        margin-top: 10px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    fromDateWrapper: {
        css: css`
            width: 50%;
            margin-right: 20px;
            [class^="FormFieldStyle"] {
                width: 100%;
                > ${FormInputWrapper} {
                    > div[class^="DateTimePickerStyle"] {
                        > div[class^="react-datetime-picker"] {
                            border: 1px solid ${ADIColors.gray3};
                            &:focus {
                                box-shadow: none;
                                outline: 1px solid ${ADIColors.gray3};
                                outline-offset: -1px;
                                border-radius: 6px;
                                border: 1px solid transparent;
                            }
                        }
                    }
                }
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-right: 10px;
                    `,
                    css`
                        margin-right: 10px;
                    `,
                    css`
                        margin-right: 10px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    toDateWrapper: {
        css: css`
            width: 50%;
            margin-right: 20px;
            [class^="FormFieldStyle"] {
                width: 100%;
                > ${FormInputWrapper} {
                    > div[class^="DateTimePickerStyle"] {
                        > div[class^="react-datetime-picker"] {
                            border: 1px solid ${ADIColors.gray3};
                            &:focus {
                                box-shadow: none;
                                outline: 1px solid ${ADIColors.gray3};
                                outline-offset: -1px;
                                border-radius: 6px;
                                border: 1px solid transparent;
                            }
                        }
                    }
                }
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-right: 0px;
                        margin-left: 10px;
                    `,
                    css`
                        margin-right: 0px;
                        margin-left: 10px;
                    `,
                    css`
                        margin-right: 0px;
                        margin-left: 10px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    btnWrapper: {
        css: css`
            width: 36%;
            padding-top: 0;
            margin-top: 6px;
            display: flex;
            button {
                width: 100%;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: 100%;
                    `,
                    css`
                        width: 100%;
                    `,
                    css`
                        width: 50%;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    clearBtnWrapper: {
        css: css`
            width: 50%;
            padding-top: 0;
            margin-right: 20px;
            margin-top: 20px;
            max-width: 190px;
            min-width: 170px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        max-width: 50%;
                        min-width: initial;
                        margin-right: 2.5px;
                    `,
                    css`
                        max-width: 50%;
                        min-width: initial;
                        margin-right: 2.5px;
                    `,
                    css`
                        margin: 30px 10px 0 20px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    searchBtnWrapper: {
        css: css`
            width: 50%;
            padding-top: 0;
            margin-top: 20px;
            max-width: 190px;
            min-width: 170px;
            text-align: right;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        max-width: 50%;
                        min-width: initial;
                        margin-left: 2.5px;
                    `,
                    css`
                        max-width: 50%;
                        min-width: initial;
                        margin-left: 2.5px;
                    `,
                    css`
                        margin-top: 30px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
};

const styles = purchaseHistoryProductSearchStyles;

export const PurchaseHistoryProductSearch: FC<Props> = ({
    myPurchaseHistory,
    myPurchaseHistoryConfig,
    setPurchaseHistoryConfig,
}) => {
    const [searchTerm, setSearchTerm] = useState("");
    const [dateRangeValue, setDateRangeValue] = useState("");
    const [toDate, setToDate] = useState("");
    const [fromDate, setFromDate] = useState("");
    const [showDatePickers, setShowDatePickers] = useState(false);
    const [openDates, setOpenDates] = React.useState(false);
    const dateRef = React.useRef(null);
    const [isCustom, setIsCustom] = React.useState(false);
    const [dayscount, setDayscount] = useState(30);
    const [dateRangeLabel, setDateRangeLabel] = useState("Last 30 days");
    const [dateRange, setDateRange] = useState("Last 30 days");

    useOutsideAlerter(dateRef, setOpenDates);

    if (!myPurchaseHistory) {
        return null;
    }

    const getTodaysDate = () => {
        const today = new Date();
        const yyyy = today.getFullYear();
        let mm = String(today.getMonth() + 1).padStart(2, "0");
        let dd = String(today.getDate()).padStart(2, "0");
        const formattedToday = mm + "/" + dd + "/" + yyyy;
        return formattedToday;
    };

    const getCurrentDate = () => {
        const today = new Date();
        const yyyy = today.getFullYear();

        let mm = String(today.getMonth() + 1).padStart(2, "0");
        let dd = String(today.getDate()).padStart(2, "0");
        const formattedToday = yyyy + "-" + mm + "-" + dd + "T00:00:00";
        return new Date(formattedToday);
    };

    const changDateRange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const value = event.target.value;
        setDateRangeValue(value);
        if (value === "Custom") {
            setShowDatePickers(true);
        } else if (value === "--Select--") {
            setToDate("");
            setFromDate("");
            setShowDatePickers(false);
        } else {
            setShowDatePickers(false);
            setToDate(getTodaysDate());
            const daysVal =
                value === "Last 2 weeks"
                    ? 14
                    : value === "Last 30 days"
                        ? 30
                        : value === "Last 60 days"
                            ? 60
                            : value === "Last 90 days"
                                ? 90
                                : null;
            var priorDate = daysVal ? new Date(new Date().setDate(new Date().getDate() - daysVal)) : null;
            if (priorDate) {
                setFromDate(convertDateFormat(priorDate))
            };
        }
    };

    const dateRangeOptions = ["Last 2 weeks", "Last 30 days", "Last 60 days", "Last 90 days", "Custom"];

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(event.target.value);
    };

    const clearSelect = () => {
        setDateRangeValue("");
        setSearchTerm("");
        setShowDatePickers(false);
        setToDate("");
        setFromDate("");
        setPurchaseHistoryConfig({
            toDate: '',
            fromDate: '',
            searchText: "",
            pageName: "purchasehistory",
            pagination: defaultPaginationValues,
        });
    };

    const searchSelect = () => {
        setPurchaseHistoryConfig({
            ...myPurchaseHistoryConfig,
            searchText: searchTerm.trim(),
            toDate: convertDateFormat(toDate),
            fromDate: convertDateFormat(fromDate),
        });
    };

    const convertDateFormat = (date: any) => {
        const formattedDate = new Date(date);
        const month = String(formattedDate.getMonth() + 1).padStart(2, "0");
        const day = String(formattedDate.getDate()).padStart(2, "0");
        const year = formattedDate.getFullYear();
        return `${year}/${month}/${day}`;
    };

    const fromDateChangeHandler = ({ selectedDay }: Pick<DatePickerState, "selectedDay">) => {
        if (selectedDay) setFromDate(convertDateFormat(selectedDay));
    };

    const fromDateSelected = fromDate ? new Date(fromDate) : undefined;

    const toDateChangeHandler = ({ selectedDay }: Pick<DatePickerState, "selectedDay">) => {
        if (selectedDay) setToDate(convertDateFormat(selectedDay));
    };

    const toDateSelected = toDate ? new Date(toDate.replace(/-/g, "/")) : undefined;

    const getDateRange = (value: string) => {
        let daysVal: number | null = null;
        let fromDate = "";
        let toDate = getTodaysDate();
        
        if (value === "Last 2 weeks") {
            daysVal = 14;
        } else if (value === "Last 30 days") {
            daysVal = 30;
        } else if (value === "Last 60 days") {
            daysVal = 60;
        } else if (value === "Last 90 days") {
            daysVal = 90;
        }
        
        if (daysVal) {
            const priorDate = new Date(new Date().setDate(new Date().getDate() - daysVal));
            fromDate = convertDateFormat(priorDate);
        }
    
        return { fromDate, toDate };
    };
    const dateRangeChangeHandler = (value: string) => {
        setIsCustom(false);
        setDateRange(value);
        
        if (value === "Custom") {
            setShowDatePickers(true);
        } else if (value === "--Select--") {
            setToDate("");
            setFromDate("");
            setShowDatePickers(false);
        } else {
            setShowDatePickers(false);
            const { fromDate, toDate } = getDateRange(value);
            setFromDate(fromDate);
            setToDate(toDate);
            setDateRangeLabel(value);
            setOpenDates(false);
        }
    };
    
    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper {...styles.searchFieldWrapper}>
                <TextField
                    value={searchTerm}
                    onChange={handleChange}
                    placeholder={translate("Enter value here...")}
                    label={translate("Search Item # or Item Name")}
                    labelPosition="top"
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            searchSelect();
                        }
                    }}
                />
            </StyledWrapper>
            <StyledWrapper {...styles.dateRangeWrapper}>
                <StyledWrapper as="label" {...styles.dropdownLabel}>
                    {translate("Date Range")}
                </StyledWrapper>
                <StyledWrapper {...styles.dropdownContainer} ref={dateRef}>
                    <StyledWrapper {...styles.dropdownValue} onClick={() => setOpenDates(!openDates)}>
                        {dateRangeLabel}
                        <Typography {...styles.icon}>
                            <DropDown />
                        </Typography>
                    </StyledWrapper>
                    {
                        openDates ? (
                            <StyledWrapper {...styles.dropdownList} as={"ul"}>
                                <StyledWrapper
                                    {...styles.dropdownValues}
                                    as={"li"}
                                    onClick={() => dateRangeChangeHandler("Last 2 weeks")}
                                    className={dateRange === "lst 2 weeks" ? "active" : "normal"}
                                >
                                    {translate("Last 2 weeks")}
                                </StyledWrapper>
                                <StyledWrapper
                                    {...styles.dropdownValues}
                                    as={"li"}
                                    onClick={() => dateRangeChangeHandler("Last 30 days")}
                                    className={dateRange === "Last 30 days" ? "active" : "normal"}
                                >
                                    {translate("Last 30 days")}
                                </StyledWrapper>

                                <StyledWrapper
                                    {...styles.dropdownValues}
                                    as={"li"}
                                    onClick={() => dateRangeChangeHandler("Last 60 days")}
                                    className={dateRange === "Last 60 days" ? "active" : "normal"}
                                >
                                    {translate("Last 60 days")}
                                </StyledWrapper>
                                <StyledWrapper
                                    {...styles.dropdownValues}
                                    as={"li"}
                                    onClick={() => dateRangeChangeHandler("Last 90 days")}
                                    className={dateRange === "Last 90 days" ? "active" : "normal"}
                                >
                                    {translate("Last 90 days")}
                                </StyledWrapper>
                                <StyledWrapper
                                    {...styles.dropdownValues}
                                    as={"li"}
                                    onClick={() => dateRangeChangeHandler("custom")}
                                    className={dateRange === "custom" ? "active" : "normal"}
                                >
                                    {translate("Custom")}
                                </StyledWrapper>
                            </StyledWrapper>
                        ) : null
                    }
                </StyledWrapper>
            </StyledWrapper>
            {dateRange === "custom" && (
                <StyledWrapper {...styles.customDateWrapper}>
                    <StyledWrapper {...styles.fromDateWrapper}>
                        <DatePicker
                            {...styles.fromDateStyles}
                            label={translate("Start Date")}
                            selectedDay={fromDateSelected}
                            onDayChange={fromDateChangeHandler}
                            format="MM/dd/y"
                            dateTimePickerProps={{
                                clearIcon: null,
                                maxDate: getCurrentDate()
                            }}
                        />
                    </StyledWrapper>
                    <StyledWrapper {...styles.toDateWrapper}>
                        <DatePicker
                            {...styles.toDateStyles}
                            label={translate("End Date")}
                            selectedDay={toDateSelected}
                            onDayChange={toDateChangeHandler}
                            format="MM/dd/y"
                            dateTimePickerProps={{
                                clearIcon: null,
                                maxDate: getCurrentDate()
                            }}
                        />
                    </StyledWrapper>
                </StyledWrapper>
            )}
            <StyledWrapper {...styles.btnWrapper}>
                <StyledWrapper {...styles.clearBtnWrapper}>
                    <Button variant="secondary" onClick={clearSelect}>
                        Clear
                    </Button>
                </StyledWrapper>
                <StyledWrapper {...styles.searchBtnWrapper}>
                    <Button variant="primary" onClick={searchSelect}>
                        Search
                    </Button>
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(PurchaseHistoryProductSearch),
    definition: {
        group: "My Account",
        displayName: "Purchase History Product Search",
        fieldDefinitions: [],
    },
};

export default widgetModule;
